<i18n>
{
  "de": {
    "reward": "Abo",
    "rewards": "Mitgliedschaften",
    "draft": "Entwurf",
    "scheduled": "Geplant",
    "published": "Veröffentlicht",
    "fans": "Fan",
    "public": "Öffentlich",
    "protected": "Geschützt",
    "expand": "Beitrag ausklappen",
    "collapse": "Beitrag einklappen",
    "post": "Beitrag",
    "accessableUntil": "Verfügbar bis",
    "comments": "Kommentare"
  },
  "en": {
    "reward": "Subscription",
    "rewards": "Memberships",
    "draft": "Draft",
    "scheduled": "Scheduled",
    "published": "Published",
    "fans": "Fan",
    "public": "Public",
    "protected": "Protected",
    "expand": "Expand post",
    "collapse": "Collapse post",
    "post": "Post",
    "accessableUntil": "Access until",
    "comments": "Comments"
  }
}
</i18n>

<template>
  <article
    v-if="post"
    class="media post-tile"
    :class="{ box: !isModal, 'post-is-sticky': sticky }"
  >
    <div
      :id="`post_${post._id}`"
      ref="measureTarget"
      :class="{ collapsed: shouldCollapse }"
      class="media-content"
    >
      <div class="content">
        <div class="post-inner-content">
          <div class="post-head">
            <div class="post-head-row">
              <div
                v-if="isModal"
                class="clickable mobile-spaced"
                @click.prevent="pushToProfile"
              >
                <img
                  class="image is-64h"
                  :src="avatarImage"
                >
              </div>
              <div class="post-textual">
                <div
                  v-if="isModal"
                  class="post-meta"
                >
                  <div class="post-nick">
                    <div v-if="myProfile">
                      <small>
                        {{ $t(postAccess) }}
                        {{ $t('post') }}
                      </small>
                      <small class="pl-5">
                        ({{ $t(post.status) }}
                        <i class="material-icons on-tile">
                          {{
                            formatStatusIcon(post.status)
                          }}
                        </i>)
                      </small>
                    </div>
                    <div v-else>
                      <span>{{ user.nickname }}</span>
                    </div>
                  </div>
                  <div class="post-date">
                    <div v-if="!isModal && !sticky">
                      <small>
                        <nuxt-link
                          :to="localePath({
                            name: 'id-post-slug-postId',
                            params: {
                              id: user.urlAlias,
                              postId: post._id,
                              slug: urlSlug(post.title),
                              title: `${user.nickname} - ${post.title}`
                            }
                          })
                          "
                          data-qa="post-link"
                          class="post-url gray-blueish on-tile"
                        >{{ formatDate(post.publishDateTime)
                        }}
                        </nuxt-link>
                      </small>
                    </div>
                    <div v-else>
                      <small
                        v-if="!sticky"
                        class="gray-blueish"
                      >
                        {{ localeDate(post.publishDateTime, { hideSuffix: true }) }}
                      </small>
                    </div>
                  </div>
                </div>
                <h4
                  v-if="!isModal"
                  class="post-title"
                >
                  <nuxt-link
                    :to="localePath({
                      name: 'id-post-slug-postId',
                      params: {
                        id: user.urlAlias,
                        postId: post._id,
                        slug: urlSlug(post.title),
                        title: `${user.nickname} - ${post.title}`
                      }
                    })
                    "
                    data-qa="post-link"
                    class="post-url"
                  >
                    <div class="is-flex is-justify-content-space-between">
                      <div>{{ post.title }}</div>
                    </div>
                  </nuxt-link>
                </h4>
                <h4
                  v-else
                  class="post-title"
                >
                  {{ post.title }}
                </h4>
                <div
                  v-if="post.tags"
                  class="tags post-tags mb-15"
                >
                  <nuxt-link
                    v-for="tag of post.tags"
                    :key="tag._id"
                    :to="localePath({
                      name: tagIds.includes(tag.id) ? 'id-posts' : 'id-posts-tag',
                      params: tagIds.includes(tag.id) ? { id: user.urlAlias } : { id: user.urlAlias, tag: tag.slug },
                    })"
                    class="tag is-light"
                    :class="{ 'is-primary': tagIds.includes(tag.id) }"
                  >
                    {{ tag.name }}
                  </nuxt-link>
                </div>
              </div>
            </div>
            <div
              :class="{ 'mb-15': !isAuthenticated }"
              class="post-text"
              v-html="post.post"
            />
          </div>
          <div
            v-if="!post.locked"
            class="post-content"
          >
            <div
              v-if="post.livestream && post.livestream.url"
              class="post-media-section"
            >
              <div v-if="!twitchChannel">
                <div
                  v-if="allowedFrameSource(post.livestream.url)"
                  style="position: relative; padding-bottom: 56.25%"
                >
                  <iframe
                    class="stream-frame"
                    width="100%"
                    height="100%"
                    frameborder="0"
                    seamless
                    allow="autoplay; fullscreen"
                    allowfullscreen="true"
                    webkitAllowFullScreen="true"
                    :src="post.livestream.url"
                  />
                </div>
                <a
                  v-else
                  :href="post.livestream.url"
                  :title="livestreamDisplayName"
                  class="file-item item"
                  target="_blank"
                >
                  <i class="material-icons">tv</i>
                  <span
                    :class="{
                      truncated: post.livestream.url === livestreamDisplayName
                    }"
                  >{{ livestreamDisplayName }}</span>
                </a>
              </div>
              <no-ssr v-else>
                <twitch-player
                  :channel="twitchChannel"
                  :refId="`twitch-video-${post._id}`"
                />
              </no-ssr>
            </div>
            <div
              v-if="post.externalLink && post.externalLink.url"
              class="post-media-section"
            >
              <a
                :href="post.externalLink.url"
                :title="externalLinkDisplayName"
                class="file-item item"
                target="_blank"
              >
                <i class="material-icons">exit_to_app</i>
                <span
                  :class="{
                    truncated: post.externalLink.url === externalLinkDisplayName
                  }"
                >{{ externalLinkDisplayName }}</span>
              </a>
            </div>
            <div
              v-if="hasMedia"
              class="post-media"
            >
              <div
                v-if="hasPDFs"
                class="pdf-gallery post-media-section"
              >
                <a
                  v-for="pdf in pdfs"
                  :key="pdf.id"
                  :href="pdf.path"
                  :title="pdf.meta.displayName"
                  class="file-item item"
                >
                  <i class="material-icons">picture_as_pdf</i>
                  <p class="item-meta">
                    <span
                      v-if="pdf.meta.displayName"
                      :class="{ truncated: pdf.path === pdf.meta.displayName }"
                    >{{
                      pdf.meta.displayName
                    }}</span>
                    <span v-else>{{ pdf.name }}</span>
                    <span v-if="pdf.meta.description">-</span>
                    {{ pdf.meta.description }}
                  </p>
                </a>
              </div>
              <div
                v-if="hasZips"
                class="zip-gallery post-media-section"
              >
                <a
                  v-for="zip in zips"
                  :key="zip.id"
                  :href="zip.path"
                  :title="zip.meta.displayName"
                  class="file-item item"
                >
                  <i class="material-icons">get_app</i>
                  <p class="item-meta">
                    <span
                      v-if="zip.meta.displayName"
                      :class="{ truncated: zip.path === zip.meta.displayName }"
                    >{{
                      zip.meta.displayName
                    }}</span>
                    <span v-else>{{ zip.name }}</span>
                    <span v-if="zip.meta.description">-</span>
                    {{ zip.meta.description }}
                  </p>
                </a>
              </div>
              <div
                v-if="hasImages"
                class="image-gallery post-media-section"
              >
                <gallery :images="images" />
              </div>
              <div
                v-if="audios.length > 0"
                class="audio-gallery post-media-section"
              >
                <div
                  v-for="(audio, idx) in audios"
                  :key="audio.id"
                  class="audio-item item"
                >
                  <audio-player
                    :audio="audio"
                    :downloadName="urlSlug(audio.meta.displayName || `${post.title} track ${idx + 1}`)"
                  />
                </div>
              </div>
              <div
                v-if="videos.length > 0"
                class="video-gallery post-media-section"
              >
                <div
                  v-for="video in videos"
                  :key="video.id"
                  class="video-item item"
                >
                  <!-- title/description render inside the player (vjs-title-overlay) -->
                  <videoPlayer :video="video" />
                </div>
              </div>
            </div>
            <div
              v-if="post.poll"
              class="mt-20"
            >
              <poll :poll="post.poll" />
            </div>
          </div>
          <div v-else>
            <post-locked
              :user="user"
              :post="post"
              :isModal="isModal"
            />
            <post-stats :post="post" />
          </div>
        </div>
        <div v-if="isModal && !post.locked && post.commentsEnabled">
          <post-comments
            :post="post"
            :displayContext="'modalComments'"
            :instantlyOpenComments="instantlyOpenComments"
            :commentData="commentData"
          />
        </div>
        <!-- Expand button integrated with collapse overlay -->
        <div
          v-if="!isModal && !post.locked && postExpandable"
          class="expand-overlay"
          :class="{ 'is-visible': shouldCollapse }"
        >
          <button
            class="btn-expand button"
            @click="toggleCollapsed"
          >
            <span class="icon">
              <i class="material-icons">{{ collapsed ? 'expand_more' : 'expand_less' }}</i>
            </span>
            <span>{{ collapsedButtonText }}</span>
          </button>
        </div>
      </div>
      <div
        v-if="!isModal"
        class="post-footer post-inner-content"
      >
        <div
          class="post-meta-row is-flex is-flex-direction-row is-justify-content-space-between"
          :class="{ 'mb-10': showAccessUntil }"
        >
          <div class="has-text-left is-flex is-flex-direction-column">
            <div
              class="pt-10"
              :class="{ 'access-until-padding': showAccessUntil }"
            >
              <small
                v-if="!sticky"
                class="post-title-date font-weight-300 gray-blueish"
              >
                {{ localeDate(post.publishDateTime, { hideSuffix: true }) }}
              </small><br>
              <small
                v-if="showAccessUntil"
                class="post-title-date font-weight-300 gray-blueish"
              >
                ({{ $t('accessableUntil') }}:
                {{ localeDate(post.accessUntil, { locale: $i18n.locale, hideSuffix: true }) }})
              </small>
            </div>
          </div>
          <div
            class="flex-grow"
            :class="{ 'access-until-padding': showAccessUntil }"
          >
            <socialShare
              class="social-share"
              :artist="user"
              :post="post"
              :fullPath="fullPath"
            />
          </div>
        </div>
      </div>
      <div
        v-if="!isModal && !post.locked && post.commentsEnabled"
        class="post-inner-content"
        style="width: 100%"
      >
        <post-comments
          :post="post"
          :displayContext="'desktopComments'"
          :commentData="commentData"
        />
      </div>
    </div>
  </article>
</template>

<script>
import gallery from '~/components/gallery'
import postStats from '~/components/post-stats'
import postLocked from '~/components/post-locked'
import postComments from '~/components/post-comments'
import socialShare from '~/components/social-share'
import videoPlayer from '~/components/video-player'
import audioPlayer from '~/components/audio-player'
import poll from '~/components/poll'
import twitchPlayer from '~/components/twitch'

import expandableContent from '~/mixins/expandable-content'

import { mapState } from 'vuex'

export default {
  name: 'ProfilePost',
  props: ['user', 'post', 'isModal', 'sticky', 'instantlyOpenComments', 'commentData'],

  mixins: [expandableContent],

  components: {
    gallery,
    postLocked,
    postStats,
    socialShare,
    videoPlayer,
    audioPlayer,
    poll,
    twitchPlayer,
    postComments
  },

  data() {
    return {
      images: [],
      videos: [],
      audios: [],
      pdfs: [],
      zips: [],
      collapsed: true,
      isMounted: false
    }
  },

  computed: {
    // TODO: Let API tell frontend what's inside
    hasMedia() {
      return (this.hasImages || this.hasAudios || this.hasVideos || this.hasPDFs || this.hasZips)
    },
    hasImages() {
      return this.images.length
    },
    hasAudios() {
      return this.audios.length
    },
    hasVideos() {
      return this.videos.length
    },
    hasPDFs() {
      return this.pdfs.length
    },
    hasZips() {
      return this.zips.length
    },
    fullPath() {
      return `https://${this.host}/${this.user.urlAlias}/post/${this.urlSlug(this.post.title)}/${this.post._id}`
    },
    postAccess() {
      if (this.post.access === 'rewards') {
        return 'protected'
      }
      return this.post.access
    },
    avatarImage() {
      if (!this.user.profile) return ''
      if (typeof this.user.profile.imgs.avatar === 'string') {
        return this.user.profile.imgs.avatar
      }

      return `/services/media/image/${this.user._id}/${this.user.profile.imgs.avatar.name}/192/0/image.webp`
    },
    myProfile() {
      if (this.isAuthenticated) {
        return this.$store.state.user.nickname === this.user.nickname
      }
      return false
    },

    mediaCount() {
      if (!this.post) return 0
      return (this.post.inlineMedia || []).length + (this.post.media || []).length
    },

    postExpandable() {
      if (!this.isMounted) return false
      return this.measuredHeight > 500 || this.mediaCount > 2
    },

    shouldCollapse() {
      return !this.isModal && !this.post.locked && this.collapsed && this.postExpandable
    },
    collapsedButtonText() {
      return this.collapsed ? this.$t('expand') : this.$t('collapse')
    },
    livestreamDisplayName() {
      if (!this.post.livestream || !this.post.livestream.url) return ''
      if (!this.post.livestream.displayName || this.post.livestream.displayName === '') {
        return this.post.livestream.url
      }
      return this.post.livestream.displayName
    },
    externalLinkDisplayName() {
      if (!this.post.externalLink || !this.post.externalLink.url) return ''
      if (!this.post.externalLink.displayName || this.post.externalLink.displayName === '') {
        return this.post.externalLink.url
      }
      return this.post.externalLink.displayName
    },
    twitchChannel() {
      if (!this.post || !this.post.livestream || !this.post.livestream.url) {
        return false
      }
      const match = this.post.livestream.url.match(/^https:\/\/www\.twitch\.tv\/([a-z0-9_]+)$/)
      if (!match || match.length < 2) return false
      return match[1]
    },
    tagIds() {
      if (!this.postFilter) return []
      return this.postFilter.tags.map((t) => t.id)
    },

    showAccessUntil() {
      return this.post.access !== 'public' && this.post.accessUntil
    },

    ...mapState(['isAuthenticated', 'host', 'postFilter'])
  },

  methods: {
    formatStatusIcon(status) {
      if (status === 'draft') return 'drafts'
      else if (status === 'scheduled') return 'hourglass_empty'
      else if (status === 'published') return 'language'
    },
    toggleCollapsed() {
      if (!this.collapsed) {
        this.$root.$emit('scrollToId', `post_${this.post._id}`)
      }
      this.collapsed = !this.collapsed
    },
    pushToProfile() {
      const path = `/${this.user.urlAlias}/posts`
      if (this.$route.path === path) {
        this.$scrollTo('#profile-navigation')
      }

      this.$router.push({
        name: `id-posts___${this.$i18n.locale}`,
        params: {
          id: this.user.urlAlias
        }
      })
    },
    allowedFrameSource(url) {
      // only allow this one
      return url.startsWith('https://gnt.ironrobots.com/')
    }
  },
  head() {
    // only the modal owns the title; in a feed the page sets it and every
    // rendered post would otherwise override it with the bare nickname
    if (!this.isModal) return {}
    return {
      title: `${this.user.nickname} - ${this.post.title}`
    }
  },

  mounted() {
    if (this.post.media) {
      this.images = this.getImages(this.post.media)
      this.videos = this.getVideos(this.post.media)
      this.audios = this.getAudios(this.post.media)
      this.pdfs = this.getPDFs(this.post.media)
      this.zips = this.getZips(this.post.media)
    }
    this.isMounted = true
    this.$emit('loaded')
  }
}
</script>

<style lang="scss">
@import "assets/sass/_variables";

// Common centered content width for post elements
.post-inner-content {
  max-width: 100ch;
  margin-left: auto;
  margin-right: auto;
}

article {
  .post-head {
    .clickable {
      cursor: pointer;
    }

    img:not(.inline-image) {
      float: left;
      margin-bottom: 0;
    }

    img.is-64h {
      height: 64px;
    }

    iframe {
      &.ql-video {
        width: 100%;
        min-height: 350px;
      }
    }

    .post-url {
      color: black;

      &.gray-blueish {
        color: $gt-gray-blueish;
      }
    }

    // Mirror the Quill editor's rendered-content baseline (margin: 0 on block elements,
    // line-height 1.5) so authored posts look the same in the editor and on the website.
    .post-text {
      font-size: clamp(1rem, 0.95rem + 0.25vw, 1.125rem);
      line-height: 1.5;

      p, ol, ul, pre, blockquote, h1, h2, h3, h4, h5, h6 {
        margin: 0;
        padding: 0;
      }

      ol, ul {
        padding-left: 1.5em;
      }
    }
  }

  .media-content {
    position: relative;

    &.collapsed {
      max-height: 450px;
      overflow-y: hidden;
    }

    .stream-frame {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
    }
  }

  // Expand overlay - gradient fade with button integrated
  .expand-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    padding-bottom: 16px;
    z-index: 10;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;

    // Gradient background only when collapsed - uses dynamic tile color
    &.is-visible {
      opacity: 1;
      pointer-events: auto;
      padding-top: 100px;
      background: linear-gradient(
        to bottom,
        transparent 0%,
        var(--tiles-background-color) 100%
      );
    }

    .btn-expand {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 10px 24px;
      font-size: 0.9rem;
      font-weight: 500;
      color: var(--secondary-text-color);
      background-color: var(--secondary-color);
      border: none;
      border-radius: 4px;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      transition: background-color 0.2s ease, transform 0.2s ease;

      &:hover {
        background: color-mix(in hsl, var(--secondary-color), var(--secondary-text-color) 10%);
        transform: scale(1.02);
      }

      .icon {
        display: flex;
        align-items: center;

        .material-icons {
          font-size: 1.25rem;
        }
      }
    }
  }

  // Post footer (date, share buttons)
  .post-footer {
    width: 100%;
    margin-top: 20px;

    .access-until-padding {
      padding-top: 30px !important;
    }
  }

  &.media.box {
    flex-flow: column nowrap;

    .media-content {
      width: 100%;
    }
  }

  .image-gallery {
    width: 100%;
  }
}

@media screen and (max-width: 548px) {
  article {
    .post-head {
      img {
        float: none;
        margin-bottom: 10px;
      }
    }
  }
}

.inline-image {
  display: inline-block;
  max-width: 100%;
  height: auto;
  vertical-align: middle;
}

@media only screen and (max-width: $mobile) {
  .post-head-row {
    font-size: 0.8rem;
    display: flex;

    .post-textual {
      width: 100%;
    }
  }

  article.media {
    &.box {
      box-shadow: none;
    }
  }
}

.post-head {
  .clickable {
    &.mobile-spaced {
      margin-right: 8px;
    }
  }

  img:not(.inline-image) {
    margin-right: 1em;
    float: left;
  }

  .post-meta {
    margin-bottom: 0.25em;
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;

    .post-nick {
      margin: 0;
      padding: 0;
      font-size: 0.9rem;
      align-self: flex-end;

      i {
        font-size: 1em;
        line-height: 1.1em;
        vertical-align: text-bottom;
        display: inline-flex;
        font-size: 1em;
        color: var(--secondary-color);
      }
    }

    .post-date {
      font-size: 0.9rem;
      text-align: right;
      align-self: flex-start;
    }
  }

  .post-title {
    font-size: clamp(1.25rem, 1.1rem + 0.5vw, 1.5rem);
    line-height: 1.35;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
  }
}

.post-content {
  overflow-x: hidden;
  padding-top: 20px;

  .truncated {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.post-media .post-media-section {
  display: flex;
  flex-flow: column nowrap;
  margin: 0px 0 0px 0px;

  &.audio-gallery {
    margin-top: 20px;
  }

  .item,
  figure {
    margin: 12px 0;

    .item-meta {
      font-size: 0.9em;
    }
  }
}
.audio-item {
  display: flex;
  flex-flow: column nowrap;

  audio {
    width: 100%;
  }
}

.file-item {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  background-color: $gt-blue-light;
  color: var(--secondary-color);
  padding: 10px;

  i {
    margin-right: 8px;
  }
}

.post-community {
  margin-top: 10px !important;
  margin-bottom: 0;

  .media-content {
    font-size: 0.9rem;
    margin-top: 0 !important;

    figure {
      margin-left: 0;
      margin-right: 10px;
    }

    .comments {
      margin-top: 20px;

      .comment-controls {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;

        &.unauthorized {
          position: relative !important;
          display: inline-block;
          font-size: 0.7rem !important;
        }
      }

      .vue-portal-target {
        margin-top: 10px;
      }
    }
  }

  .hidden {
    display: none !important;
  }

  .small {
    // font-size: 0.8rem;
    padding-left: 30px;

    .material-icons {
      &.icon-comment {
        font-size: 1.5rem;
      }
    }
  }

  // .material-icons {
  //   &.icon-heart {
  //     font-size: .8rem;
  //   }
  //   &.icon-comment {
  //     font-size: .6rem;
  //     margin-right: 3px;
  //   }
  // }
}
</style>
